import { useEffect } from 'react';
import { reportSafeDiagnostic } from './safeDiagnostics.js';

const THEME_TOKENS = {
  dark:{
    bg:'#0B1220',
    surface:'#121B2C',
    surface2:'#182338',
    surface3:'#1F2C45',
    border:'rgba(148,163,184,0.18)',
    borderStrong:'rgba(148,163,184,0.30)',
    text:'#E8EEF7',
    text2:'#AAB6C8',
    text2Rgb:'170,182,200',
    text3:'#8D9AB0',
    text3Rgb:'141,154,176',
    info:'#6EA8FE',
    infoRgb:'110,168,254',
    success:'#57C98A',
    successRgb:'87,201,138',
    warning:'#E8B34B',
    warningRgb:'232,179,75',
    danger:'#E2685F',
    dangerRgb:'226,104,95',
    focus:'0 0 0 2px rgba(110,168,254,0.35)',
    selectedBg:'rgba(110,168,254,0.14)',
    selectedBorder:'rgba(110,168,254,0.45)',
  },
  light:{
    bg:'#EEEFF1',
    surface:'#FFFFFF',
    surface2:'#F6F7F8',
    surface3:'#F2F3F5',
    border:'rgba(17,19,24,0.22)',
    borderStrong:'rgba(17,19,24,0.50)',
    text:'#111318',
    text2:'#363A42',
    text2Rgb:'54,58,66',
    text3:'#4E535C',
    text3Rgb:'78,83,92',
    info:'#1D4ED8',
    infoRgb:'29,78,216',
    success:'#05703F',
    successRgb:'5,112,63',
    warning:'#8A5300',
    warningRgb:'138,83,0',
    danger:'#B42318',
    dangerRgb:'180,35,24',
    focus:'0 0 0 2px rgba(29,78,216,0.70)',
    selectedBg:'rgba(29,78,216,0.10)',
    selectedBorder:'rgba(29,78,216,0.70)',
  },
  'high-contrast':{
    bg:'#05070B',
    surface:'#101827',
    surface2:'#162238',
    surface3:'#1B2A43',
    border:'#A5B4C9',
    borderStrong:'#D6E2F2',
    text:'#FFFFFF',
    text2:'#F1F5F9',
    text2Rgb:'241,245,249',
    text3:'#D4DFED',
    text3Rgb:'212,223,237',
    info:'#9CC5FF',
    infoRgb:'156,197,255',
    success:'#8EE3B7',
    successRgb:'142,227,183',
    warning:'#FFD479',
    warningRgb:'255,212,121',
    danger:'#FF9B91',
    dangerRgb:'255,155,145',
    focus:'0 0 0 3px #FFD479',
    selectedBg:'rgba(156,197,255,0.25)',
    selectedBorder:'#D2E5FF',
  },
  comfort:{
    bg:'#F1EFE8',
    surface:'#FCFAF5',
    surface2:'#F5F1E8',
    surface3:'#EAE5DA',
    border:'#75818A',
    borderStrong:'#64717B',
    text:'#202B31',
    text2:'#3F505A',
    text2Rgb:'63,80,90',
    text3:'#586671',
    text3Rgb:'88,102,113',
    info:'#2A638A',
    infoRgb:'42,99,138',
    success:'#236F58',
    successRgb:'35,111,88',
    warning:'#94621B',
    warningRgb:'148,98,27',
    danger:'#A7443A',
    dangerRgb:'167,68,58',
    focus:'0 0 0 3px rgba(42,99,138,0.38)',
    selectedBg:'rgba(42,99,138,0.12)',
    selectedBorder:'rgba(42,99,138,0.55)',
  },
};

const THEME_TOKEN_NAMES = ['bg', 'surface', 'surface2', 'surface3', 'border', 'borderStrong', 'text', 'text2', 'text2Rgb', 'text3', 'text3Rgb', 'info', 'infoRgb', 'success', 'successRgb', 'warning', 'warningRgb', 'danger', 'dangerRgb', 'focus', 'selectedBg', 'selectedBorder'];

function cssName(name) {
  return name.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`);
}

function tokenBlock(tokens) {
  return `${THEME_TOKEN_NAMES.map(name => `    --${cssName(name)}:${tokens[name]};`).join('\n')}\n    --focus-ring:${tokens.focus};`;
}

// UI foundation tokens: one declaration sheet per theme, emitted by
// uiTokenBlock() inside each :root block below. THEME_TOKENS stays exactly as
// it is, because other checks read it by regex.
// - Tag tones --tag-<tone>-bg/-fg. Blue is reserved for Transaction; indigo
//   marks work in progress and draft edits (never amber).
// - Money --amount-in/-out/-bonus; risk band fills with --band-on label text.
// - Time context: Now, In period, Frozen. --breach-fg colours only a breached
//   threshold.
// - --swatch-1..8 with -on text are the palette for configured colours.
// Every text and background pair reaches 4.5:1 in its own theme
// (scripts/ui-foundation/verify-ui-tokens.mjs).
const UI_TOKENS = {
  dark:`
    --tag-neutral-bg:#263248;--tag-neutral-fg:#E8EEF7;--tag-light-bg:#1B2538;--tag-light-fg:#AAB6C8;--tag-outline-border:#6B7B95;
    --tag-indigo-bg:#2A2E63;--tag-indigo-fg:#D3DAFF;--tag-blue-bg:#153562;--tag-blue-fg:#C3DDFF;
    --tag-green-bg:#123D2B;--tag-green-fg:#A8EFC6;--tag-amber-bg:#3F300E;--tag-amber-fg:#FBDB8E;
    --tag-orange-bg:#46280F;--tag-orange-fg:#FDC49A;--tag-red-bg:#4A2121;--tag-red-fg:#FDB4AE;
    --amount-in:#6BD49B;--amount-out:#F39A92;--amount-bonus:#AEB8FF;
    --band-low:#4FBF83;--band-medium:#E3AE45;--band-high:#F08A4B;--band-critical:#F2726A;--band-on:#0B1220;
    --now-bg:var(--tag-neutral-bg);--now-fg:var(--tag-neutral-fg);--period-bg:var(--tag-indigo-bg);--period-fg:var(--tag-indigo-fg);
    --frozen-border:var(--tag-outline-border);--edited-bg:var(--tag-indigo-bg);--edited-fg:var(--tag-indigo-fg);--breach-fg:#F28B82;
    --swatch-1:#1E3A66;--swatch-1-on:#C9DEFF;--swatch-2:#0F4743;--swatch-2-on:#A9F0E5;--swatch-3:#1A4630;--swatch-3-on:#B4F0CB;--swatch-4:#4A3812;--swatch-4-on:#FBDB8E;
    --swatch-5:#53300F;--swatch-5-on:#FDC9A1;--swatch-6:#562626;--swatch-6-on:#FDBDB7;--swatch-7:#3B2A63;--swatch-7-on:#DCC9FF;--swatch-8:#55233F;--swatch-8-on:#FBC2DE;
  `,
  light:`
    --tag-neutral-bg:#E7E8EB;--tag-neutral-fg:#2E3239;--tag-light-bg:#F1F2F4;--tag-light-fg:#454951;--tag-outline-border:#7D828B;
    --tag-indigo-bg:#E4E2FA;--tag-indigo-fg:#362C93;--tag-blue-bg:#D5E5FF;--tag-blue-fg:#0A43A0;
    --tag-green-bg:#CBEFD5;--tag-green-fg:#0A5A30;--tag-amber-bg:#FBE6A9;--tag-amber-fg:#673F00;
    --tag-orange-bg:#FFDCC4;--tag-orange-fg:#853000;--tag-red-bg:#FFD8D4;--tag-red-fg:#971B13;
    --amount-in:#0A5A30;--amount-out:#971B13;--amount-bonus:#3730A3;
    --band-low:#22A55B;--band-medium:#D69E0B;--band-high:#EF6C1F;--band-critical:#F0605A;--band-on:#111318;
    --now-bg:var(--tag-neutral-bg);--now-fg:var(--tag-neutral-fg);--period-bg:var(--tag-indigo-bg);--period-fg:var(--tag-indigo-fg);
    --frozen-border:var(--tag-outline-border);--edited-bg:var(--tag-indigo-bg);--edited-fg:var(--tag-indigo-fg);--breach-fg:#B42318;
    --swatch-1:#DBEAFE;--swatch-1-on:#1E3A8A;--swatch-2:#CCFBF1;--swatch-2-on:#115E59;--swatch-3:#DCFCE7;--swatch-3-on:#14532D;--swatch-4:#FEF3C7;--swatch-4-on:#78350F;
    --swatch-5:#FFEDD5;--swatch-5-on:#7C2D12;--swatch-6:#FEE2E2;--swatch-6-on:#7F1D1D;--swatch-7:#F3E8FF;--swatch-7-on:#581C87;--swatch-8:#FCE7F3;--swatch-8-on:#831843;
  `,
  'high-contrast':`
    --tag-neutral-bg:#1F2E48;--tag-neutral-fg:#FFFFFF;--tag-light-bg:#162238;--tag-light-fg:#F1F5F9;--tag-outline-border:#A5B4C9;
    --tag-indigo-bg:#2B2F7A;--tag-indigo-fg:#FFFFFF;--tag-blue-bg:#0F3A75;--tag-blue-fg:#FFFFFF;
    --tag-green-bg:#0D4A31;--tag-green-fg:#FFFFFF;--tag-amber-bg:#4D3800;--tag-amber-fg:#FFFFFF;
    --tag-orange-bg:#5A2C08;--tag-orange-fg:#FFFFFF;--tag-red-bg:#5E1A17;--tag-red-fg:#FFFFFF;
    --amount-in:#8EE3B7;--amount-out:#FF9B91;--amount-bonus:#C3CBFF;
    --band-low:#8EE3B7;--band-medium:#FFD479;--band-high:#FFB077;--band-critical:#FF9B91;--band-on:#05070B;
    --now-bg:var(--tag-neutral-bg);--now-fg:var(--tag-neutral-fg);--period-bg:var(--tag-indigo-bg);--period-fg:var(--tag-indigo-fg);
    --frozen-border:var(--tag-outline-border);--edited-bg:var(--tag-indigo-bg);--edited-fg:var(--tag-indigo-fg);--breach-fg:#FF9B91;
    --swatch-1:#0F3A75;--swatch-1-on:#FFFFFF;--swatch-2:#0B4A45;--swatch-2-on:#FFFFFF;--swatch-3:#0D4A31;--swatch-3-on:#FFFFFF;--swatch-4:#4D3800;--swatch-4-on:#FFFFFF;
    --swatch-5:#5A2C08;--swatch-5-on:#FFFFFF;--swatch-6:#5E1A17;--swatch-6-on:#FFFFFF;--swatch-7:#3F2A75;--swatch-7-on:#FFFFFF;--swatch-8:#5E1C44;--swatch-8-on:#FFFFFF;
  `,
  comfort:`
    --tag-neutral-bg:#ECE8DF;--tag-neutral-fg:#3F505A;--tag-light-bg:#F1EDE4;--tag-light-fg:#4A5961;--tag-outline-border:#75818A;
    --tag-indigo-bg:#E9E9F7;--tag-indigo-fg:#3B3A84;--tag-blue-bg:#DCE8F3;--tag-blue-fg:#1F4E79;
    --tag-green-bg:#DDEFE3;--tag-green-fg:#1F5E46;--tag-amber-bg:#F6EAC8;--tag-amber-fg:#7A4B0E;
    --tag-orange-bg:#F8E3D0;--tag-orange-fg:#8A3A13;--tag-red-bg:#F6DEDA;--tag-red-fg:#8E2E26;
    --amount-in:#1F6450;--amount-out:#9A3B32;--amount-bonus:#4B4A99;
    --band-low:#4CB07C;--band-medium:#C9962A;--band-high:#E3803F;--band-critical:#E9786E;--band-on:#202B31;
    --now-bg:var(--tag-neutral-bg);--now-fg:var(--tag-neutral-fg);--period-bg:var(--tag-indigo-bg);--period-fg:var(--tag-indigo-fg);
    --frozen-border:var(--tag-outline-border);--edited-bg:var(--tag-indigo-bg);--edited-fg:var(--tag-indigo-fg);--breach-fg:#A7443A;
    --swatch-1:#DCE8F3;--swatch-1-on:#1F4E79;--swatch-2:#D5EFEA;--swatch-2-on:#1E5E58;--swatch-3:#DDEFE3;--swatch-3-on:#1F5E46;--swatch-4:#F6EAC8;--swatch-4-on:#6E430C;
    --swatch-5:#F8E3D0;--swatch-5-on:#7A3412;--swatch-6:#F6DEDA;--swatch-6-on:#7F2A22;--swatch-7:#EAE2F3;--swatch-7-on:#53337A;--swatch-8:#F5E0EA;--swatch-8-on:#7A2D52;
  `,
};

// Layout widths are the same in every theme. Fixed tracks use these custom
// properties with @container collapse, so MobileCollapse.css does not change.
const UI_LAYOUT_TOKENS = '--ui-board-max:1440px;--ui-rail-w:216px;--ui-steps-w:220px;--ui-inspector-w:420px;--ui-drawer-w:420px;--ui-drawer-wide-w:760px;--ui-dialog-w:560px;--ui-dialog-wide-w:1040px;';

function uiTokenBlock(theme) {
  const lines = UI_TOKENS[safeThemeName(theme)].split('\n').map(line => line.trim()).filter(Boolean);
  return [...lines, UI_LAYOUT_TOKENS].map(line => `    ${line}`).join('\n');
}

function hexToRgb(color) {
  const value = String(color || '').trim();
  const match = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return null;
  const hex = match[1].length === 3
    ? match[1].split('').map(char => char + char).join('')
    : match[1];
  const number = Number.parseInt(hex, 16);
  return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
}

function channel(value) {
  const normalized = value / 255;
  return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

function luminance(rgb) {
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]);
}

function contrastRatio(a, b) {
  const one = hexToRgb(a);
  const two = hexToRgb(b);
  if (!one || !two) return null;
  const l1 = luminance(one);
  const l2 = luminance(two);
  const light = Math.max(l1, l2);
  const dark = Math.min(l1, l2);
  return (light + 0.05) / (dark + 0.05);
}

function safeThemeName(theme) {
  return Object.prototype.hasOwnProperty.call(THEME_TOKENS, theme) ? theme : 'dark';
}

function rgbString(color) {
  const rgb = hexToRgb(color);
  return rgb ? rgb.join(',') : '';
}

function hexString(rgb) {
  if (!rgb) return '';
  return `#${rgb.map(value => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0')).join('')}`;
}

function mixRgb(from, to, amount) {
  return from.map((value, index) => value + (to[index] - value) * amount);
}

function contrastSafeColor(color, surface, fallback) {
  const rgb = hexToRgb(color);
  const surfaceRgb = hexToRgb(surface);
  if (!rgb || !surfaceRgb) return fallback || color;
  if ((contrastRatio(color, surface) || 0) >= 4.5) return color;
  const target = luminance(surfaceRgb) < 0.5 ? [255, 255, 255] : [0, 0, 0];
  for (let step = 1; step <= 12; step += 1) {
    const candidate = hexString(mixRgb(rgb, target, step / 12));
    if ((contrastRatio(candidate, surface) || 0) >= 4.5) return candidate;
  }
  return fallback || color;
}

// The selected tint a branding primary gets in each theme (the current rail
// item, tab and selected row). Light matches its own selectedBg/selectedBorder.
function brandSelectedAlpha(theme) {
  if (theme === 'high-contrast') return { bg:'0.25', border:'0.9' };
  if (theme === 'light') return { bg:'0.10', border:'0.70' };
  return { bg:'0.14', border:'0.45' };
}

// A branding primary must be readable on the card, on the page background and
// on its own selected tint over the page (the current rail item draws the
// accent there). The surface check alone kept #794BEB at 3.67:1 on that tint.
function brandAccentColor(color, theme = 'dark') {
  const tokens = THEME_TOKENS[safeThemeName(theme)];
  const readable = contrastSafeColor(color, tokens.surface, tokens.info);
  const bgRgb = hexToRgb(tokens.bg);
  const alpha = Number(brandSelectedAlpha(theme).bg);
  const fits = candidate => {
    const tint = hexString(mixRgb(bgRgb, hexToRgb(candidate), alpha));
    return (contrastRatio(candidate, tokens.surface) || 0) >= 4.5 && (contrastRatio(candidate, tokens.bg) || 0) >= 4.5 && (contrastRatio(candidate, tint) || 0) >= 4.5;
  };
  if (!hexToRgb(readable) || !bgRgb || fits(readable)) return readable;
  const target = luminance(bgRgb) < 0.5 ? [255, 255, 255] : [0, 0, 0];
  for (let step = 1; step <= 24; step += 1) {
    const candidate = hexString(mixRgb(hexToRgb(readable), target, step / 24));
    if (fits(candidate)) return candidate;
  }
  return readable;
}

function brandingOverrides(branding = {}, theme = 'dark') {
  const tokens = THEME_TOKENS[safeThemeName(theme)];
  const lines = [];
  const primary = String(branding.primaryColor || '').trim();
  const accent = String(branding.accentColor || '').trim();
  if (hexToRgb(primary)) {
    const readable = brandAccentColor(primary, theme);
    const selected = brandSelectedAlpha(theme);
    lines.push(`    --info:${readable};`);
    lines.push(`    --info-rgb:${rgbString(readable)};`);
    lines.push(`    --on-info:${contrastRatio(readable, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#000000'};`);
    // Text actions (ui-text-button, row links, player ids) read --link. Light
    // pins it to the default blue, so with a branding primary it follows --info
    // here, or one page shows two accent hues. Links also sit on the page
    // background, so the colour stays readable there too.
    lines.push(`    --link:${contrastSafeColor(readable, tokens.bg, readable)};`);
    lines.push(`    --selected-bg:rgba(${rgbString(readable)},${selected.bg});`);
    lines.push(`    --selected-border:rgba(${rgbString(readable)},${selected.border});`);
  }
  if (hexToRgb(accent)) {
    const readable = contrastSafeColor(accent, tokens.surface, tokens.success);
    lines.push(`    --success:${readable};`);
    lines.push(`    --success-rgb:${rgbString(readable)};`);
  }
  return lines.length ? lines.join('\n') : '';
}

function warnIfBrandingBreaksContrast(branding = {}, theme = 'dark') {
  const tokens = THEME_TOKENS[safeThemeName(theme)];
  [
    ['primaryColor', branding.primaryColor, tokens.surface],
    ['accentColor', branding.accentColor, tokens.surface],
  ].forEach(([name, color, surface]) => {
    if (!color) return;
    const readable = contrastSafeColor(color, surface, color);
    const ratio = contrastRatio(readable, surface);
    if (ratio !== null && ratio < 4.5) {
      reportSafeDiagnostic('warn', 'theme_branding_contrast_failed', { stage:'resolve_theme_contrast' });
    }
  });
}

export { THEME_TOKENS, UI_TOKENS, UI_LAYOUT_TOKENS, tokenBlock, uiTokenBlock, contrastRatio, contrastSafeColor, brandAccentColor, brandingOverrides };

export default function AppGlobalStyles({ branding = {}, theme = 'dark' }) {
  const activeTheme = safeThemeName(theme);
  const overrideCss = brandingOverrides(branding, activeTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = activeTheme;
    document.documentElement.style.colorScheme = activeTheme === 'light' || activeTheme === 'comfort' ? 'light' : 'dark';
    warnIfBrandingBreaksContrast(branding, activeTheme);
  }, [activeTheme, branding]);

  return (
    <style>{`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  :root,:root[data-theme="dark"]{
${tokenBlock(THEME_TOKENS.dark)}
${uiTokenBlock('dark')}
    --radius:6px;--radius-lg:10px;
    --space-1:4px;--space-2:8px;--space-3:12px;--space-4:16px;--space-5:24px;
    --font-size-meta:12px;--font-size-body:14px;--font-size-section:16px;--font-size-page:22px;--font-size-metric:24px;
    --font:'DM Sans',system-ui,sans-serif;
    --mono:'JetBrains Mono',monospace;
    --primary:var(--info);
    --accent:var(--success);
    --link:var(--info);--on-info:#0B1220;--disabled:var(--text3);--overlay:rgba(3,7,18,.72);
    --control-border:var(--border);
    --shadow:none;--shadow-md:none;--shadow-rgb:0,0,0;--shadow-overlay:0 18px 48px rgba(0,0,0,.45);
    --risk-critical:var(--danger);--risk-high:var(--danger);--risk-medium:var(--warning);--risk-low:var(--success);--risk-unknown:var(--text3);
    --priority-urgent:var(--danger);--priority-high:var(--danger);--priority-medium:var(--warning);--priority-low:var(--info);--priority-normal:var(--text2);
    --status-success:var(--success);--status-warning:var(--warning);--status-danger:var(--danger);--status-info:var(--info);--status-neutral:var(--text2);
    --confidence-high:var(--success);--confidence-medium:var(--warning);--confidence-low:var(--text2);--confidence-unknown:var(--text3);
    --chart-1:#6EA8FE;--chart-2:#E8B34B;--chart-3:#57C98A;--chart-4:#E2685F;--chart-5:#B794F4;--chart-5-rgb:183,148,244;--chart-6:#4FD1C5;--chart-7:#F687B3;--chart-8:#AAB6C8;
  }
  :root[data-theme="light"]{
${tokenBlock(THEME_TOKENS.light)}
${uiTokenBlock('light')}
    --radius:6px;--radius-lg:10px;
    --space-1:4px;--space-2:8px;--space-3:12px;--space-4:16px;--space-5:24px;
    --font-size-meta:12px;--font-size-body:14px;--font-size-section:16px;--font-size-page:22px;--font-size-metric:24px;
    --font:'DM Sans',system-ui,sans-serif;
    --mono:'JetBrains Mono',monospace;
    --primary:var(--info);
    --accent:var(--success);
    --link:#1D4ED8;--on-info:#FFFFFF;--disabled:#7E838C;--overlay:rgba(17,19,24,.58);
    --control-border:var(--border-strong);
    --shadow:0 1px 2px rgba(17,19,24,.08);--shadow-md:none;--shadow-rgb:17,19,24;--shadow-overlay:0 18px 48px rgba(17,19,24,.22);
    --risk-critical:var(--danger);--risk-high:var(--danger);--risk-medium:var(--warning);--risk-low:var(--success);--risk-unknown:var(--text3);
    --priority-urgent:var(--danger);--priority-high:var(--danger);--priority-medium:var(--warning);--priority-low:var(--info);--priority-normal:var(--text2);
    --status-success:var(--success);--status-warning:var(--warning);--status-danger:var(--danger);--status-info:var(--info);--status-neutral:var(--text2);
    --confidence-high:var(--success);--confidence-medium:var(--warning);--confidence-low:var(--text2);--confidence-unknown:var(--text3);
    --chart-1:#2563EB;--chart-2:#A35E00;--chart-3:#05703F;--chart-4:#B42318;--chart-5:#7C3AED;--chart-5-rgb:124,58,237;--chart-6:#0F766E;--chart-7:#BE185D;--chart-8:#5B606A;
  }
  :root[data-theme="high-contrast"]{
${tokenBlock(THEME_TOKENS['high-contrast'])}
${uiTokenBlock('high-contrast')}
    --radius:6px;--radius-lg:10px;
    --space-1:4px;--space-2:8px;--space-3:12px;--space-4:16px;--space-5:24px;
    --font-size-meta:12px;--font-size-body:14px;--font-size-section:16px;--font-size-page:22px;--font-size-metric:24px;
    --font:'DM Sans',system-ui,sans-serif;--mono:'JetBrains Mono',monospace;
    --primary:var(--info);--accent:var(--success);--link:var(--info);--on-info:#081322;--disabled:var(--text3);--overlay:rgba(0,0,0,.88);
    --control-border:var(--border);
    --shadow:none;--shadow-md:none;--shadow-rgb:0,0,0;--shadow-overlay:0 18px 48px rgba(0,0,0,.8);
    --risk-critical:var(--danger);--risk-high:var(--danger);--risk-medium:var(--warning);--risk-low:var(--success);--risk-unknown:var(--text3);
    --priority-urgent:var(--danger);--priority-high:var(--danger);--priority-medium:var(--warning);--priority-low:var(--info);--priority-normal:var(--text2);
    --status-success:var(--success);--status-warning:var(--warning);--status-danger:var(--danger);--status-info:var(--info);--status-neutral:var(--text2);
    --confidence-high:var(--success);--confidence-medium:var(--warning);--confidence-low:var(--text2);--confidence-unknown:var(--text3);
    --chart-1:#9CC5FF;--chart-2:#FFD479;--chart-3:#8EE3B7;--chart-4:#FF9B91;--chart-5:#D5B6FF;--chart-5-rgb:213,182,255;--chart-6:#80E4E0;--chart-7:#FFB5D5;--chart-8:#E2E8F0;
  }
  :root[data-theme="comfort"]{
${tokenBlock(THEME_TOKENS.comfort)}
${uiTokenBlock('comfort')}
    --radius:8px;--radius-lg:12px;
    --space-1:4px;--space-2:8px;--space-3:12px;--space-4:17px;--space-5:26px;
    --font-size-meta:12px;--font-size-body:15px;--font-size-section:17px;--font-size-page:23px;--font-size-metric:25px;
    --font:'DM Sans',system-ui,sans-serif;--mono:'JetBrains Mono',monospace;
    --primary:var(--info);--accent:var(--success);--link:var(--info);--on-info:#FFFFFF;--disabled:var(--text3);--overlay:rgba(25,36,42,.58);
    --control-border:var(--border);
    --shadow:none;--shadow-md:none;--shadow-rgb:25,36,42;--shadow-overlay:0 18px 48px rgba(25,36,42,.22);
    --risk-critical:var(--danger);--risk-high:var(--danger);--risk-medium:var(--warning);--risk-low:var(--success);--risk-unknown:var(--text3);
    --priority-urgent:var(--danger);--priority-high:var(--danger);--priority-medium:var(--warning);--priority-low:var(--info);--priority-normal:var(--text2);
    --status-success:var(--success);--status-warning:var(--warning);--status-danger:var(--danger);--status-info:var(--info);--status-neutral:var(--text2);
    --confidence-high:var(--success);--confidence-medium:var(--warning);--confidence-low:var(--text2);--confidence-unknown:var(--text3);
    --chart-1:#2A638A;--chart-2:#94621B;--chart-3:#236F58;--chart-4:#A7443A;--chart-5:#7855A1;--chart-5-rgb:120,85,161;--chart-6:#246F72;--chart-7:#994C78;--chart-8:#52646D;
  }
  :root[data-theme="${activeTheme}"]{
${overrideCss}
  }
  html{font-size:100%;text-size-adjust:100%}html,body,#root{min-height:100%;background:var(--bg);color:var(--text);font-family:var(--font);font-size:var(--font-size-body);line-height:1.5;-webkit-font-smoothing:antialiased}body{min-width:320px;overflow-wrap:break-word}
  :root[data-theme="comfort"] body,:root[data-theme="comfort"] #root{line-height:1.6}
  /* Light: dark text on a light page keeps the platform's normal smoothing;
     antialiased turns off stem darkening on macOS and thins 12px text. */
  :root[data-theme="light"] body,:root[data-theme="light"] #root{-webkit-font-smoothing:auto}
  /* anywhere and break-word wrap the same text the same way, and differ on one
     thing that matters everywhere else: anywhere makes an element's min-content
     width a single character. Flex and grid honour min-content, so every button
     and every field in the application was free to shrink below its own label -
     which is why a Save button rendered as "Sav e" and Deactivate as
     "Deactivat e" whenever the row beside it grew. break-word keeps min-content
     at the longest word, so a control cannot be squeezed narrower than the word
     it is showing. Long unbroken strings that need to break mid-token opt in
     below. */
  .app-identifier,[data-identifier],code,kbd,samp{overflow-wrap:anywhere}
  /* A form control's default width comes from its size attribute, not from its
     container, and a grid item does not shrink below min-content on its own.
     Together those put a number input rendered at roughly 170px inside a 130px
     track, overlapping whatever sits in the next column - which is what the
     help text beside Threshold was colliding with. */
  input,select,textarea{min-width:0;max-width:100%}
  *{box-sizing:border-box}
  input,textarea,select{font-family:var(--font)!important;font-size:var(--font-size-body)!important;color:var(--text)!important;background:var(--surface3)!important;border:1px solid var(--control-border)!important;border-radius:var(--radius)!important;padding:7px 9px!important;outline:2px solid transparent!important;outline-offset:2px!important;transition:border-color .15s,box-shadow .15s!important}
  input:focus,textarea:focus,select:focus{border-color:var(--info)!important}
  input:focus-visible,textarea:focus-visible,select:focus-visible,button:focus-visible,[role="button"]:focus-visible,a:focus-visible{outline:2px solid transparent!important;outline-offset:2px!important;box-shadow:var(--focus-ring)!important}
  ::placeholder{color:var(--text3)!important;opacity:1!important}
  button{font-family:var(--font)!important;cursor:pointer!important;transition:background-color .12s,border-color .12s,color .12s,box-shadow .12s,opacity .12s!important}
  button:disabled{opacity:0.4!important;cursor:not-allowed!important}
  button[aria-disabled="true"],[role="button"][aria-disabled="true"]{cursor:not-allowed!important}
  ::selection{background:var(--selected-bg);color:var(--text)}
  ::-webkit-scrollbar{width:6px;height:6px}
  ::-webkit-scrollbar-track{background:transparent}
  ::-webkit-scrollbar-thumb{background:var(--border-strong);border-radius:99px}
  ::-webkit-scrollbar-thumb:hover{background:var(--text3)}
  .app-skip-link{position:fixed;left:12px;top:8px;z-index:3000;transform:translateY(-140%);background:var(--surface);border:2px solid var(--info);border-radius:6px;color:var(--text);font-weight:700;padding:8px 12px;text-decoration:none;transition:transform .12s}
  .app-skip-link:focus{transform:translateY(0)}
  .app-shell{width:100%;max-width:100%;min-height:100vh;display:flex;min-width:0}
  .app-shell__body{--ui-topbar-h:54px;width:calc(100% - 76px);max-width:100%;flex:1;margin-left:76px;display:flex;flex-direction:column;min-height:100vh;min-width:0}
  .app-shell__main{width:100%;max-width:100%;flex:1;display:flex;flex-direction:column;min-width:0;outline:2px solid transparent;outline-offset:-2px}
  .app-shell__main h1[tabindex="-1"]:focus-visible{outline:2px solid var(--info);outline-offset:4px}
  .app-route-announcement{position:fixed;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
  .app-route-fallback-heading{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
  .app-topbar__shortcut{margin-left:auto;border:1px solid var(--border-strong);border-radius:4px;background:var(--surface2);color:var(--text3);font:12px var(--mono);padding:1px 5px}
  .app-identifier,[data-identifier]{font-family:var(--mono)}
  .app-topbar{position:sticky;top:0;z-index:90;min-height:54px;display:flex;align-items:center;gap:12px;flex-shrink:0;padding:0 20px;background:var(--bg);border-bottom:1px solid var(--border)}
  .app-topbar__search{position:relative;flex:1;max-width:440px}.app-topbar__search-button{width:100%;display:flex;align-items:center;gap:7px;padding:7px 12px;color:var(--text2);background:var(--surface3);border:1px solid var(--border);border-radius:var(--radius-lg);font-size:var(--font-size-body);text-align:left}.app-topbar__actions{display:flex;align-items:center;gap:8px;margin-left:auto;min-width:0}.app-topbar__context{max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding:4px 8px;color:var(--text3);background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius);font-size:var(--font-size-meta)}
  @media (max-width:1100px){
    .field-catalog-layout{grid-template-columns:minmax(250px,280px) minmax(0,1fr)!important}
  }
  @media (max-width:760px){
    .field-catalog-layout{grid-template-columns:minmax(0,1fr)!important;grid-template-rows:minmax(220px,36vh) minmax(0,1fr)!important;overflow:auto!important}
    .field-catalog-rail{border-right:0!important;border-bottom:1px solid var(--border)!important}
    .field-catalog-main{padding:14px!important;overflow:visible!important}
    .segments-workspace__header{padding:12px 12px 0!important}
    .segments-workspace__header-row,.segments-workspace__section-heading{align-items:flex-start!important;flex-wrap:wrap!important}
    .segments-workspace__header-row>div:first-child,.segments-workspace__section-heading>div:first-child{flex:1 0 100%}
    .segments-workspace__tabs{max-width:100%;overflow-x:auto;padding-bottom:2px}
    .segments-workspace__tabs>button{flex:0 0 auto}
    .segments-workspace__body{padding:12px!important}
    .segments-workspace__editor-layout{align-items:stretch!important;flex-direction:column!important}
    .segments-workspace__editor-layout>div{width:100%!important;max-width:100%!important}
    .segments-workspace__group-row,.segments-workspace__identity-grid,.segments-workspace__run-policy-grid,.segments-workspace__lifecycle-grid,.segments-workspace__governance-grid,.segments-workspace__preview-grid{grid-template-columns:minmax(0,1fr)!important}
    .segments-workspace__choice-row{flex-wrap:wrap!important}
    .segments-workspace__choice-row>button{white-space:normal;text-align:left}
    .segments-workspace__preview-result,.segments-workspace__validation-row{grid-template-columns:minmax(0,1fr)!important}
    .field-import-workspace__header,.document-generator-workspace__header{align-items:flex-start!important;flex-wrap:wrap!important;padding:12px!important}
    .field-import-workspace__actions,.document-generator-workspace__actions{max-width:100%;flex-wrap:wrap!important}
    .field-import-workspace__layout,.document-generator-workspace__layout{grid-template-columns:minmax(0,1fr)!important;grid-template-rows:minmax(180px,32vh) minmax(0,1fr)!important;overflow:auto!important}
    .field-import-workspace__library,.document-generator-workspace__library{border-right:0!important;border-bottom:1px solid var(--border)!important}
    .field-import-workspace__main,.document-generator-workspace__main{overflow:visible!important;padding:12px!important}
    .field-import-workspace__editor-grid,.document-generator-workspace__editor-grid{grid-template-columns:minmax(0,1fr)!important}
    .field-import-workspace__run-panel,.document-generator-workspace__run-panel{position:static!important}
    .manual-suggestion-rules{grid-template-columns:minmax(0,1fr)!important;min-height:0!important}
    .manual-suggestion-rules__library{max-height:300px;overflow:auto;border-right:0!important;border-bottom:1px solid var(--border)!important}
    .population-metrics-workspace__main{padding:14px 12px!important;overflow:visible!important}
    .population-metrics-workspace__responsive-grid{grid-template-columns:minmax(0,1fr)!important}
    .population-filter-node>div:first-child{align-items:flex-start!important;flex-direction:column!important}
    .population-filter-node>div:first-child>div{max-width:100%;flex-wrap:wrap!important}
    .grouped-option-library__footer{align-items:stretch!important;flex-direction:column!important}
    .grouped-option-library__footer-actions{flex-wrap:wrap!important}
    .grouped-option-library__footer>button{width:100%}
    .review-templates-workspace__library{position:static!important;width:100%!important;max-width:100%!important}
    .review-templates-workspace__editor{min-width:0!important;flex-basis:100%!important}
  }
  @media (max-width:1100px){
    .app-topbar{height:auto!important;min-height:54px;padding:8px 12px!important;flex-wrap:wrap}
    .app-topbar__search{max-width:none!important;min-width:min(260px,calc(100vw - 92px));order:3;flex-basis:100%!important}
    .app-topbar__actions{max-width:100%;overflow-x:auto;padding-bottom:1px}
    .manual-review-header{position:static!important}
    .population-metrics-workspace{grid-template-columns:minmax(0,1fr)!important;grid-template-rows:minmax(220px,32vh) minmax(0,1fr)!important;overflow:auto!important}
    .population-metrics-workspace__library{border-right:0!important;border-bottom:1px solid var(--border)!important}
  }
  @media (max-width:520px){
    .app-topbar__search-label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .app-topbar__shortcut{display:none}
    .app-topbar__actions>span{display:none!important}
    .app-shell__main>div{max-width:100%}
    .manual-review-field-row__grid{grid-template-columns:minmax(0,1fr)!important}
    .manual-review-field-row__options{justify-content:flex-start!important}
    .manual-review-field-row__options>button{max-width:100%;white-space:normal;text-align:left}
  }
`}</style>
  );
}
